import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";

const LAST_KEY = "madio.lastActivity";
const EVENTS = ["pointerdown", "keydown", "wheel", "touchstart", "scroll"];

const now = () => Date.now();
const readLast = () => {
  try { return Number(localStorage.getItem(LAST_KEY)) || now(); } catch { return now(); }
};

/**
 * On a shared computer (ticked at sign-in), sign out after the company's idle limit with nobody using the CRM, in
 * any of its tabs (the last activity is shared through localStorage). The server enforces the same limit on the
 * session (sessions.py), so a tab left closed is signed out too; this is what ends it while a tab stays open, since
 * the screens' own background refreshes keep the session alive on the server.
 */
export default function IdleSignOut() {
  const { user, session, logout } = useAuth();
  const hours = session?.shared ? Number(session.idle_hours) || 0 : 0;
  const logoutRef = useRef(logout);
  logoutRef.current = logout;
  const signedIn = Boolean(user);

  useEffect(() => {
    if (!signedIn || !hours) return undefined;
    const limit = hours * 3600 * 1000;
    let lastWrite = 0;
    const mark = () => {
      const t = now();
      if (t - lastWrite < 15000) return;           // a write every 15 s at most
      lastWrite = t;
      try { localStorage.setItem(LAST_KEY, String(t)); } catch { /* storage off */ }
    };
    const check = () => {
      if (now() - readLast() > limit) {
        logoutRef.current(`Signed out after ${hours} hour${hours === 1 ? "" : "s"} without use on a shared computer.`);
      }
    };
    mark();
    EVENTS.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    document.addEventListener("visibilitychange", check);
    const timer = window.setInterval(check, 30000);
    return () => {
      EVENTS.forEach((e) => window.removeEventListener(e, mark));
      document.removeEventListener("visibilitychange", check);
      window.clearInterval(timer);
    };
  }, [signedIn, hours]);

  return null;
}
